import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft, FolderOpen, Save, Loader2, Search, Info,
  FileText, Wrench, Download, Plus, Trash2, CalendarIcon, Upload, Lock
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ALL_SNS_CODES } from '../data/snsData';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ISSUES = ['4.1', '4.2', '5.0', '6.0'];
const SECURITY_OPTIONS = [
  { value: '01', label: '01 - Unclassified' },
  { value: '02', label: '02 - Restricted' },
  { value: '03', label: '03 - Confidential' },
  { value: '04', label: '04 - Secret' },
  { value: '05', label: '05 - Top Secret' },
];

const DM_SCHEMA_TYPES = [
  "Applicability Cross Reference (ACT)",
  "BRDocs", "BREX", "Checklist", "Common Repository",
  "Conditions Cross Reference (CCT)", "Crew", "Descriptive",
  "Fault", "Illustrated Parts Data (IPD)", "Learning",
  "Procedural", "Process", "Product Cross Reference (PCT)",
  "Schedule", "Service Bulletin", "Wiring Data", "Wiring Fields",
];

// BRDPs that need NO additional information field
const NO_ADDITIONAL_INFO = new Set(['BRDP-S1-00001']);
// BRDPs that are auto-set (non-editable)
const AUTO_SET_BRDPS = new Set(['BRDP-S1-00003']);
// BRDPs with special field types
const SPECIAL_BRDPS = new Set(['BRDP-S1-00002', 'BRDP-S1-00006', 'BRDP-S1-00008', 'BRDP-S1-00009']);

/* ─── Searchable Select for SNS Codes ─── */
const SnsSelect = ({ value, onChange, testId }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return ALL_SNS_CODES;
    const q = search.toLowerCase();
    return ALL_SNS_CODES.filter(o => `${o.code} - ${o.name || o.title || ''}`.toLowerCase().includes(q));
  }, [search]);

  const selectedOption = ALL_SNS_CODES.find(o => o.code === value);

  return (
    <div className="relative">
      <div
        className="flex items-center w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:border-teal-400 transition-colors h-9"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={testId}
      >
        <span className={`flex-1 truncate font-mono ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
          {selectedOption ? `${selectedOption.code} - ${selectedOption.name || selectedOption.title || ''}` : 'Select System Code...'}
        </span>
        <Search className="w-3.5 h-3.5 text-muted-foreground ml-2 shrink-0" />
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg">
          <div className="p-2 border-b">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SNS codes..." className="h-8 text-sm" autoFocus
              onClick={(e) => e.stopPropagation()} />
          </div>
          <ScrollArea className="h-64">
            <div className="p-1">
              {filtered.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3 text-center">No results</p>
              ) : filtered.map((o) => (
                <div key={o.code}
                  className={`px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-teal-50 transition-colors font-mono ${value === o.code ? 'bg-teal-100 text-teal-700' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onChange(o.code); setIsOpen(false); setSearch(''); }}>
                  {o.code} - {o.name || o.title || ''}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => { setIsOpen(false); setSearch(''); }} />}
    </div>
  );
};

/* ─── BRDP Row Component ─── */
const BrdpRow = ({ brdp, decision, onChange, selectedIssue, locked }) => {
  const answer = decision?.answer || '';
  const additionalFields = decision?.additional_fields || {};
  const isYes = answer === 'yes';
  const isNo = answer === 'no';
  const isAutoSet = AUTO_SET_BRDPS.has(brdp.id);
  const hasSpecialFields = SPECIAL_BRDPS.has(brdp.id);
  const noAdditionalInfo = NO_ADDITIONAL_INFO.has(brdp.id);
  const isLocked = locked || isAutoSet;

  // BRDP-S1-00002: Company/CAGE multi-entry
  const cageEntries = additionalFields.cage_entries || [{ company_name: '', cage_code: '' }];
  const handleCageAdd = () => {
    const updated = { ...additionalFields, cage_entries: [...cageEntries, { company_name: '', cage_code: '' }] };
    onChange(brdp.id, 'yes', updated);
  };
  const handleCageRemove = (idx) => {
    const updated = { ...additionalFields, cage_entries: cageEntries.filter((_, i) => i !== idx) };
    onChange(brdp.id, 'yes', updated);
  };
  const handleCageChange = (idx, field, value) => {
    const newEntries = [...cageEntries];
    newEntries[idx] = { ...newEntries[idx], [field]: value };
    onChange(brdp.id, 'yes', { ...additionalFields, cage_entries: newEntries });
  };

  // BRDP-S1-00006: Schemas multi-entry
  const [schemaToAdd, setSchemaToAdd] = useState('');
  const schemas = additionalFields.schemas || [];
  const handleSchemaAdd = () => {
    if (!schemaToAdd || schemas.includes(schemaToAdd)) return;
    onChange(brdp.id, 'yes', { ...additionalFields, schemas: [...schemas, schemaToAdd] });
    setSchemaToAdd('');
  };
  const handleSchemaRemove = (idx) => {
    onChange(brdp.id, 'yes', { ...additionalFields, schemas: schemas.filter((_, i) => i !== idx) });
  };

  // BRDP-S1-00008: Deliverables multi-entry
  const deliverables = additionalFields.deliverables || [''];
  const handleDeliverableAdd = () => {
    onChange(brdp.id, 'yes', { ...additionalFields, deliverables: [...deliverables, ''] });
  };
  const handleDeliverableRemove = (idx) => {
    onChange(brdp.id, 'yes', { ...additionalFields, deliverables: deliverables.filter((_, i) => i !== idx) });
  };
  const handleDeliverableChange = (idx, value) => {
    const updated = [...deliverables];
    updated[idx] = value;
    onChange(brdp.id, 'yes', { ...additionalFields, deliverables: updated });
  };

  // BRDP-S1-00009: Dates multi-entry
  const dates = additionalFields.delivery_dates || [new Date().toISOString().split('T')[0]];
  const handleDateAdd = () => {
    onChange(brdp.id, 'yes', { ...additionalFields, delivery_dates: [...dates, new Date().toISOString().split('T')[0]] });
  };
  const handleDateRemove = (idx) => {
    onChange(brdp.id, 'yes', { ...additionalFields, delivery_dates: dates.filter((_, i) => i !== idx) });
  };
  const handleDateChange = (idx, date) => {
    if (!date) return;
    const updated = [...dates];
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    updated[idx] = `${y}-${m}-${d}`;
    onChange(brdp.id, 'yes', { ...additionalFields, delivery_dates: updated });
  };

  // Auto-set display text for BRDP-S1-00003
  const autoSetText = isAutoSet && selectedIssue ? `Issue ${selectedIssue}` : '';

  return (
    <div className={`flex items-start gap-3 py-2.5 px-3 border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${isNo ? 'opacity-50' : ''} ${isLocked ? 'bg-slate-50/80' : ''}`} data-testid={`brdp-row-${brdp.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-teal-700 font-semibold whitespace-nowrap group relative cursor-help" data-testid={`brdp-id-${brdp.id}`}>
            {brdp.id}
            {brdp.reference && (
              <span className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-slate-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap z-50 shadow-lg">
                {brdp.reference}
              </span>
            )}
          </span>
          <span className="text-sm font-medium text-slate-800">{brdp.title}</span>
          {isAutoSet && isYes && <Badge className="bg-slate-100 text-slate-600 text-[10px]">{autoSetText}</Badge>}
          {locked && <Lock className="w-3 h-3 text-blue-400" />}
        </div>
        {brdp.definition && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{brdp.definition}</p>}

        {/* Locked decision detail text (imported from spreadsheet) */}
        {isLocked && additionalFields?.notes && (
          <p className="mt-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-100 whitespace-pre-wrap" data-testid={`brdp-locked-note-${brdp.id}`}>{additionalFields.notes}</p>
        )}

        {/* Imported note context for unanswered BRDPs */}
        {!isLocked && additionalFields?.imported_note && (
          <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200" data-testid={`brdp-imported-note-${brdp.id}`}>
            Source: {additionalFields.imported_note}
          </p>
        )}

        {/* BRDP-S1-00002: Company/CAGE multi-entry */}
        {isYes && brdp.id === 'BRDP-S1-00002' && (
          <div className="mt-2 space-y-2">
            {cageEntries.map((entry, idx) => (
              <div key={idx} className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500 whitespace-nowrap">Company</Label>
                  <Input value={entry.company_name || ''} onChange={(e) => handleCageChange(idx, 'company_name', e.target.value)} placeholder="Company Name" className="h-7 text-xs w-40" data-testid={`brdp-company-${brdp.id}-${idx}`} />
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500 whitespace-nowrap">CAGE</Label>
                  <Input value={entry.cage_code || ''} onChange={(e) => handleCageChange(idx, 'cage_code', e.target.value.toUpperCase().slice(0, 5))} placeholder="5 chars" className="h-7 text-xs w-24 font-mono uppercase" maxLength={5} data-testid={`brdp-cage-${brdp.id}-${idx}`} />
                </div>
                {cageEntries.length > 1 && (
                  <button onClick={() => handleCageRemove(idx)} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`brdp-cage-remove-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={handleCageAdd} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1" data-testid={`brdp-cage-add-${brdp.id}`}>
              <Plus className="w-3 h-3" /> Add another entry
            </button>
          </div>
        )}

        {/* BRDP-S1-00006: Schemas multi-entry with select */}
        {isYes && brdp.id === 'BRDP-S1-00006' && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <Select value={schemaToAdd || 'placeholder_none'} onValueChange={(v) => setSchemaToAdd(v === 'placeholder_none' ? '' : v)}>
                <SelectTrigger className="h-7 text-xs w-64" data-testid="brdp-schema-select">
                  <SelectValue placeholder="Select schema..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder_none" className="text-slate-400">Select schema...</SelectItem>
                  {DM_SCHEMA_TYPES.filter(s => !schemas.includes(s)).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={handleSchemaAdd} disabled={!schemaToAdd} className="h-7 text-xs px-2.5 border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="brdp-schema-add-btn">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            {schemas.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {schemas.map((s, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-md border border-teal-200" data-testid={`brdp-schema-tag-${idx}`}>
                    {s}
                    <button onClick={() => handleSchemaRemove(idx)} className="text-teal-400 hover:text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BRDP-S1-00008: Deliverables multi-entry */}
        {isYes && brdp.id === 'BRDP-S1-00008' && (
          <div className="mt-2 space-y-2">
            {deliverables.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <textarea
                  value={item}
                  onChange={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                    handleDeliverableChange(idx, e.target.value);
                  }}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
                  placeholder="Enter deliverable..."
                  rows={1}
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none overflow-hidden min-h-[28px]"
                  data-testid={`brdp-deliverable-${idx}`}
                />
                {deliverables.length > 1 && (
                  <button onClick={() => handleDeliverableRemove(idx)} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`brdp-deliverable-remove-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={handleDeliverableAdd} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1" data-testid={`brdp-deliverable-add`}>
              <Plus className="w-3 h-3" /> Add another deliverable
            </button>
          </div>
        )}

        {/* BRDP-S1-00009: Delivery dates with calendar */}
        {isYes && brdp.id === 'BRDP-S1-00009' && (
          <div className="mt-2 space-y-2">
            {dates.map((dateStr, idx) => {
              const dateVal = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
              return (
                <div key={idx} className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="inline-flex items-center gap-1.5 h-7 px-2.5 border rounded-md text-xs bg-white hover:border-teal-400 transition-colors" data-testid={`brdp-date-trigger-${idx}`}>
                        <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-mono">{dateStr}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={dateVal} onSelect={(d) => handleDateChange(idx, d)} initialFocus />
                    </PopoverContent>
                  </Popover>
                  {dates.length > 1 && (
                    <button onClick={() => handleDateRemove(idx)} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`brdp-date-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            <button onClick={handleDateAdd} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1" data-testid={`brdp-date-add`}>
              <Plus className="w-3 h-3" /> Add another date
            </button>
          </div>
        )}

        {/* Generic decision documentation for "Yes" BRDPs (not special, not no-info, not auto-set) */}
        {isYes && !isLocked && !hasSpecialFields && !noAdditionalInfo && !isAutoSet && (
          <div className="mt-2">
            <textarea
              value={additionalFields.notes || ''}
              onChange={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
                onChange(brdp.id, 'yes', { ...additionalFields, notes: e.target.value });
              }}
              ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
              placeholder="Document the decision details..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none overflow-hidden min-h-[56px]"
              rows={2}
              data-testid={`brdp-notes-${brdp.id}`}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 pt-0.5">
        {isLocked ? (
          /* Locked: show decision as read-only */
          <span className={`px-3 py-1 rounded-md text-xs font-semibold ${isYes ? 'bg-emerald-600 text-white' : isNo ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-testid={`brdp-locked-${brdp.id}`}>
            {isYes ? 'Yes' : isNo ? 'No' : '—'}
          </span>
        ) : (
          <>
            <button onClick={() => onChange(brdp.id, answer === 'yes' ? '' : 'yes', answer === 'yes' ? {} : additionalFields)} className={`px-2.5 py-1 rounded-l-md text-xs font-semibold border transition-colors ${isYes ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-300 hover:border-emerald-400 hover:text-emerald-600'}`} data-testid={`brdp-yes-${brdp.id}`}>
              Yes
            </button>
            <button onClick={() => onChange(brdp.id, answer === 'no' ? '' : 'no', {})} className={`px-2.5 py-1 rounded-r-md text-xs font-semibold border transition-colors ${isNo ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-500 border-slate-300 hover:border-red-400 hover:text-red-600'}`} data-testid={`brdp-no-${brdp.id}`}>
              No
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── BRDPs Tab ─── */
const BrdpTab = ({ projectId, token, onIssueChange, onDocGenerated }) => {
  const [issue, setIssue] = useState('');
  const [brdps, setBrdps] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [lockedIds, setLockedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [hasPredefined, setHasPredefined] = useState(null); // null = not answered, true/false
  const [uploading, setUploading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'unanswered'
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const fileInputRef = React.useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Auto-restore saved issue on mount (so returning to this tab shows previous state)
  useEffect(() => {
    const loadSavedState = async () => {
      try {
        const res = await axios.get(`${API}/api/brex-builder/saved-state/${projectId}`, { headers });
        if (res.data.issue && res.data.has_decisions) {
          setIssue(prev => prev || res.data.issue);
        }
      } catch { /* No saved state */ }
    };
    loadSavedState();
  }, [projectId]);

  const loadBrdps = useCallback(async (iss) => {
    if (!iss) return;
    setLoading(true);
    try {
      const [brdpRes, decRes] = await Promise.all([
        axios.get(`${API}/api/brex-builder/brdps/${iss}`, { headers }),
        axios.get(`${API}/api/brex-builder/decisions/${projectId}?issue=${iss}`, { headers }),
      ]);
      setBrdps(brdpRes.data);
      const decMap = {};
      const locked = new Set();
      (decRes.data.decisions || []).forEach(d => {
        decMap[d.brdp_id] = d;
        if (d.locked) locked.add(d.brdp_id);
      });
      // Auto-set BRDP-S1-00003
      if (!decMap['BRDP-S1-00003'] || !decMap['BRDP-S1-00003'].answer) {
        decMap['BRDP-S1-00003'] = { brdp_id: 'BRDP-S1-00003', answer: 'yes', additional_fields: {} };
      }
      setDecisions(decMap);
      setLockedIds(locked);
      // Auto-detect state based on saved decisions
      if (locked.size > 0) {
        setHasPredefined(true);
        setImportSummary({ decided: locked.size, unanswered: brdpRes.data.length - locked.size });
      } else if (Object.values(decMap).some(d => d.answer)) {
        // Decisions exist from previous session - skip the predefined prompt
        setHasPredefined(false);
      }
    } catch {
      toast.error('Failed to load BRDPs');
    } finally { setLoading(false); }
  }, [projectId, headers]);

  useEffect(() => { if (issue) loadBrdps(issue); }, [issue, loadBrdps]);
  useEffect(() => { onIssueChange?.(issue); }, [issue, onIssueChange]);

  const handleChange = (brdpId, answer, additionalFields) => {
    if (lockedIds.has(brdpId)) return;
    setDecisions(prev => ({
      ...prev,
      [brdpId]: { brdp_id: brdpId, answer, additional_fields: additionalFields || {} },
    }));
  };

  const handleSave = async () => {
    if (!issue) return;
    setSaving(true);
    try {
      const decisionsList = Object.values(decisions).filter(d => d.answer).map(d => ({
        ...d,
        locked: lockedIds.has(d.brdp_id) || undefined,
      }));
      await axios.post(`${API}/api/brex-builder/decisions`, {
        project_id: projectId,
        issue,
        decisions: decisionsList,
      }, { headers });
      toast.success('Decisions saved successfully');
    } catch { toast.error('Failed to save decisions'); }
    finally { setSaving(false); }
  };

  const handleGeneratePdf = async () => {
    if (!issue) { toast.error('Please select an S1000D Issue first'); return; }
    setGeneratingPdf(true);
    try {
      // Save decisions first
      const decisionsList = Object.values(decisions).filter(d => d.answer).map(d => ({
        ...d,
        locked: lockedIds.has(d.brdp_id) || undefined,
      }));
      await axios.post(`${API}/api/brex-builder/decisions`, {
        project_id: projectId, issue, decisions: decisionsList,
      }, { headers });
      // Generate PDF
      const res = await axios.post(`${API}/api/brex-builder/generate-brdp-pdf`, {
        project_id: projectId, issue,
      }, { headers, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `BRDP_Summary_${issue}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('BRDP PDF generated and added to Generated Documents');
      onDocGenerated?.();
    } catch (err) {
      const errorMsg = err.response?.data instanceof Blob
        ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
        : err.response?.data?.detail || 'Failed to generate BRDP PDF';
      toast.error(errorMsg);
    } finally { setGeneratingPdf(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/brex-builder/import-decisions`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const { decisions: imported, summary } = res.data;
      const newDecisions = { ...decisions };
      const newLocked = new Set(lockedIds);
      imported.forEach(d => {
        if (d.locked && d.answer) {
          newDecisions[d.brdp_id] = {
            brdp_id: d.brdp_id,
            answer: d.answer,
            additional_fields: d.additional_fields || {},
            locked: true,
          };
          newLocked.add(d.brdp_id);
        } else if (d.additional_fields?.imported_note) {
          // Keep existing answer if any, but add imported note
          if (!newDecisions[d.brdp_id]?.answer) {
            newDecisions[d.brdp_id] = {
              brdp_id: d.brdp_id,
              answer: '',
              additional_fields: d.additional_fields,
            };
          }
        }
      });
      // Auto-set BRDP-S1-00003
      if (!newDecisions['BRDP-S1-00003']?.answer) {
        newDecisions['BRDP-S1-00003'] = { brdp_id: 'BRDP-S1-00003', answer: 'yes', additional_fields: {} };
      }
      setDecisions(newDecisions);
      setLockedIds(newLocked);
      setImportSummary(summary);
      toast.success(`Imported: ${summary.decided} decided, ${summary.unanswered} unanswered`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import spreadsheet');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredBrdps = useMemo(() => {
    let list = brdps;
    // Apply view mode filter
    if (viewMode === 'unanswered') {
      list = list.filter(b => {
        const dec = decisions[b.id];
        return !dec?.answer || (!lockedIds.has(b.id) && !dec?.answer);
      });
    }
    // Apply search filter
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        b.id.toLowerCase().includes(q) ||
        b.title.toLowerCase().includes(q) ||
        b.definition?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [brdps, search, viewMode, decisions, lockedIds]);

  const stats = useMemo(() => {
    const yes = Object.values(decisions).filter(d => d.answer === 'yes').length;
    const no = Object.values(decisions).filter(d => d.answer === 'no').length;
    const locked = lockedIds.size;
    return { yes, no, total: brdps.length, pending: brdps.length - yes - no, locked };
  }, [decisions, brdps, lockedIds]);

  return (
    <div className="space-y-4" data-testid="brdp-tab">
      {/* Issue selector row */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">S1000D Issue</Label>
          <Select value={issue} onValueChange={(v) => { setIssue(v); setHasPredefined(null); setImportSummary(null); setLockedIds(new Set()); }}>
            <SelectTrigger className="w-48" data-testid="issue-select">
              <SelectValue placeholder="Select Issue..." />
            </SelectTrigger>
            <SelectContent>
              {ISSUES.map(i => <SelectItem key={i} value={i}>Issue {i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {issue && hasPredefined !== null && (
          <>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search BRDPs..." className="pl-10" data-testid="brdp-search" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-decisions-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {saving ? 'Saving...' : 'Save Decisions'}
            </Button>
            <Button onClick={handleGeneratePdf} disabled={generatingPdf || !Object.values(decisions).some(d => d.answer)} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="generate-brdp-pdf-btn">
              {generatingPdf ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
              {generatingPdf ? 'Generating...' : 'Generate PDF'}
            </Button>
          </>
        )}
      </div>

      {/* Predefined BRDPs prompt - shown after issue selected */}
      {issue && !loading && hasPredefined === null && (
        <Card className="border-teal-200 bg-teal-50/30">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-slate-800">Does this project have predefined BRDPs?</p>
                  <p className="text-sm text-slate-500 mt-0.5">If you have an existing BRDP spreadsheet with decisions already made, import it to pre-populate locked decisions.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button size="sm" onClick={() => setHasPredefined(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="has-predefined-yes">
                    <Upload className="w-4 h-4 mr-1.5" />Yes, import spreadsheet
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setHasPredefined(false)} data-testid="has-predefined-no">
                    No, start from scratch
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload area - shown when user says yes to predefined */}
      {issue && hasPredefined === true && !importSummary && (
        <Card className="border-dashed border-2 border-teal-200 bg-teal-50/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold text-slate-800 text-sm">Import BRDP Spreadsheet</p>
                <p className="text-xs text-slate-500 mt-0.5">Upload an .xlsx or .xls file with BRDP decisions. Decided BRDPs will be locked as read-only.</p>
              </div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" data-testid="brdp-file-input" />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="upload-brdp-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploading ? 'Importing...' : 'Choose File'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setHasPredefined(false); }} className="text-slate-500" data-testid="skip-import-btn">
                Skip
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import summary */}
      {importSummary && (
        <div className="flex items-center gap-2 text-sm px-1">
          <Badge className="bg-blue-100 text-blue-700">{importSummary.decided} Imported (locked)</Badge>
          <Badge variant="outline">{importSummary.unanswered} To be decided</Badge>
          <button onClick={() => fileInputRef.current?.click()} className="text-xs text-teal-600 hover:text-teal-800 ml-2 underline" data-testid="re-import-btn">
            Re-import
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
        </div>
      )}

      {/* Stats + Show All/Unanswered toggle */}
      {issue && !loading && brdps.length > 0 && (hasPredefined !== null) && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 text-sm">
            <Badge variant="secondary">{stats.total} BRDPs</Badge>
            <Badge className="bg-emerald-100 text-emerald-700">{stats.yes} Yes</Badge>
            <Badge className="bg-red-100 text-red-700">{stats.no} No</Badge>
            <Badge variant="outline">{stats.pending} Pending</Badge>
            {stats.locked > 0 && <Badge className="bg-blue-100 text-blue-700">{stats.locked} Locked</Badge>}
          </div>
          <div className="flex items-center bg-slate-100 rounded-md p-0.5" data-testid="view-mode-toggle">
            <button onClick={() => setViewMode('all')} className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${viewMode === 'all' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="view-mode-all">
              Show All
            </button>
            <button onClick={() => setViewMode('unanswered')} className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${viewMode === 'unanswered' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="view-mode-unanswered">
              Unanswered
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      )}

      {/* BRDP grid */}
      {issue && !loading && brdps.length > 0 && (hasPredefined !== null) && (
        <Card className="border-slate-200">
          <CardHeader className="py-2 px-3 bg-slate-50 border-b">
            <div className="grid grid-cols-[1fr_80px] items-center">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">BRDP Identifier / Title / Description</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Decision</span>
            </div>
          </CardHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-420px)]" style={{ minHeight: '400px' }}>
            <CardContent className="p-0">
              {filteredBrdps.map(brdp => (
                <BrdpRow key={brdp.id} brdp={brdp} decision={decisions[brdp.id]} onChange={handleChange} selectedIssue={issue} locked={lockedIds.has(brdp.id)} />
              ))}
              {filteredBrdps.length === 0 && (
                <div className="p-8 text-center text-slate-400 text-sm">
                  {viewMode === 'unanswered' ? 'All BRDPs have been answered' : 'No BRDPs match your search'}
                </div>
              )}
            </CardContent>
          </div>
        </Card>
      )}

      {!issue && (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-12 text-center text-slate-500">
            <Info className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="font-medium">Select an S1000D Issue to load Business Rule Decision Points</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── BREX Module Tab ─── */
const BrexModuleTab = ({ projectId, project, token, selectedIssue, onDocGenerated }) => {
  const [form, setForm] = useState({
    model_ident_code: '', system_diff_code: '',
    system_code: '', sub_system_code: '0', sub_sub_system_code: '0',
    assy_code: '00',
    disassy_code: '00', disassy_code_variant: 'A',
    info_code: '022', info_code_variant: 'A', item_location_code: 'D',
    security_classification: '01', issue_number: '001', in_work: '00',
    responsible_partner_company: '', responsible_partner_cage: '',
    originator: '', originator_cage: '',
  });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load saved settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/brex-builder/module-settings/${projectId}`, { headers });
        const saved = res.data?.settings;
        if (saved && Object.keys(saved).length > 0) {
          setForm(prev => ({ ...prev, ...saved }));
          return;
        }
      } catch { /* Settings not found, using defaults */ }
      // Fallback: populate from project if no saved settings
      if (project) {
        setForm(prev => ({
          ...prev,
          model_ident_code: project.model_ident_codes?.[0] || project.modelIdentCode || '',
          system_diff_code: project.system_difference_code || 'A',
        }));
      }
    };
    loadSettings();
  }, [projectId, project, headers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/brex-builder/module-settings`, {
        project_id: projectId,
        settings: form,
      }, { headers });
      toast.success('Module settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const fetchModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/brex-builder/modules/${projectId}`, { headers });
      setModules(res.data);
    } catch { /* Modules list not loaded */ }
  }, [projectId, headers]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  // Build company options from project settings
  const companyOptions = useMemo(() => {
    const opts = [];
    (project?.responsible_partner_companies || []).forEach(c => {
      opts.push({ value: c.company_name, label: `${c.company_name} (${c.cage_code})`, cage: c.cage_code });
    });
    (project?.originators || []).forEach(c => {
      if (!opts.find(o => o.value === c.company_name)) {
        opts.push({ value: c.company_name, label: `${c.company_name} (${c.cage_code})`, cage: c.cage_code });
      }
    });
    return opts;
  }, [project]);

  const handleRpcChange = (val) => {
    if (val === '__none__') {
      setForm(p => ({ ...p, responsible_partner_company: '', responsible_partner_cage: '' }));
    } else {
      const opt = companyOptions.find(o => o.value === val);
      setForm(p => ({ ...p, responsible_partner_company: val, responsible_partner_cage: opt?.cage || '' }));
    }
  };

  const handleOriginatorChange = (val) => {
    if (val === '__none__') {
      setForm(p => ({ ...p, originator: '', originator_cage: '' }));
    } else {
      const opt = companyOptions.find(o => o.value === val);
      setForm(p => ({ ...p, originator: val, originator_cage: opt?.cage || '' }));
    }
  };

  const handleGenerate = async () => {
    if (!form.model_ident_code) { toast.error('Model Identification Code is required'); return; }
    if (!form.system_code) { toast.error('System Code is required'); return; }
    const issueToUse = selectedIssue || '5.0';
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/brex-builder/generate`, {
        project_id: projectId,
        issue: issueToUse,
        ...form,
        language_iso_code: project?.language_iso_code || 'en',
        country_iso_code: project?.country_iso_code || 'US',
        applicability: project?.applicability || 'All',
      }, { headers, responseType: 'blob' });

      // Download the file
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'BREX_Module.XML';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('BREX Module generated and downloaded');
      onDocGenerated?.();
    } catch (err) {
      const errorMsg = err.response?.data instanceof Blob
        ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
        : err.response?.data?.detail || 'Failed to generate BREX module';
      toast.error(errorMsg);
    } finally { setGenerating(false); }
  };

  const handleDelete = async (moduleId) => {
    try {
      await axios.delete(`${API}/api/brex-builder/modules/${moduleId}`, { headers });
      toast.success('Module deleted');
      fetchModules();
    } catch { toast.error('Failed to delete module'); }
  };

  const modelIdentCodes = project?.model_ident_codes || (project?.modelIdentCode ? [project.modelIdentCode] : []);
  const dmcPreview = `${form.model_ident_code || '????'}-${form.system_diff_code || '?'}-${form.system_code || '??'}-${form.sub_system_code}${form.sub_sub_system_code}-${form.assy_code}-${form.disassy_code}${form.disassy_code_variant}-${form.info_code}${form.info_code_variant}-${form.item_location_code}`;

  return (
    <div className="space-y-6" data-testid="brex-module-tab">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>Create BREX Data Module</CardTitle>
          <CardDescription>
            DMC Preview: <span className="font-mono text-teal-700 font-semibold">{dmcPreview}</span>
            {selectedIssue && <span className="ml-3 text-xs text-slate-400">(Issue {selectedIssue})</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Model Ident Code */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Model Ident Code</Label>
              {modelIdentCodes.length > 0 ? (
                <Select value={form.model_ident_code} onValueChange={(v) => setForm(p => ({ ...p, model_ident_code: v }))}>
                  <SelectTrigger data-testid="mic-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {modelIdentCodes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.model_ident_code} onChange={(e) => setForm(p => ({ ...p, model_ident_code: e.target.value.toUpperCase() }))} placeholder="e.g. AS09" className="font-mono" maxLength={14} data-testid="mic-input" />
              )}
            </div>
            {/* System Diff Code */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">System Diff Code</Label>
              <Input value={form.system_diff_code} onChange={(e) => setForm(p => ({ ...p, system_diff_code: e.target.value.toUpperCase() }))} className="font-mono" maxLength={4} data-testid="sdc-input" />
            </div>
            {/* System Code - searchable SNS dropdown */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">System Code (SNS)</Label>
              <SnsSelect value={form.system_code} onChange={(v) => setForm(p => ({ ...p, system_code: v }))} testId="sys-code-select" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sub-System Code</Label>
              <Input value={form.sub_system_code} onChange={(e) => setForm(p => ({ ...p, sub_system_code: e.target.value }))} className="font-mono" maxLength={1} data-testid="subsys-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sub-Sub-System Code</Label>
              <Input value={form.sub_sub_system_code} onChange={(e) => setForm(p => ({ ...p, sub_sub_system_code: e.target.value }))} className="font-mono" maxLength={1} data-testid="subsubsys-input" />
            </div>
            {/* Assy Code */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assembly Code</Label>
              <Input value={form.assy_code} onChange={(e) => setForm(p => ({ ...p, assy_code: e.target.value }))} className="font-mono" maxLength={4} placeholder="00" data-testid="assy-code-input" />
            </div>
            {/* Disassy */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Disassembly Code</Label>
              <Input value={form.disassy_code} onChange={(e) => setForm(p => ({ ...p, disassy_code: e.target.value }))} className="font-mono" maxLength={2} placeholder="00" data-testid="disassy-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Disassy Code Variant</Label>
              <Select value={form.disassy_code_variant} onValueChange={(v) => setForm(p => ({ ...p, disassy_code_variant: v }))}>
                <SelectTrigger data-testid="disassy-var-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Info Code - fixed 022 */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Information Code</Label>
              <Input value="022" disabled className="font-mono bg-slate-100 cursor-not-allowed" data-testid="info-code-input" />
            </div>
            {/* Info Code Variant */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Info Code Variant</Label>
              <Select value={form.info_code_variant} onValueChange={(v) => setForm(p => ({ ...p, info_code_variant: v }))}>
                <SelectTrigger data-testid="info-var-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Item Location Code - fixed D */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Item Location Code</Label>
              <Input value="D" disabled className="font-mono bg-slate-100 cursor-not-allowed" data-testid="ilc-input" />
            </div>
            {/* Security */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Security Classification</Label>
              <Select value={form.security_classification} onValueChange={(v) => setForm(p => ({ ...p, security_classification: v }))}>
                <SelectTrigger data-testid="security-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECURITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Issue Number */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Issue Number</Label>
              <Input value={form.issue_number} onChange={(e) => setForm(p => ({ ...p, issue_number: e.target.value }))} className="font-mono" maxLength={3} placeholder="001" data-testid="issue-num-input" />
            </div>
            {/* In Work */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">In Work</Label>
              <Input value={form.in_work} onChange={(e) => setForm(p => ({ ...p, in_work: e.target.value }))} className="font-mono" maxLength={2} placeholder="00" data-testid="in-work-input" />
            </div>
            {/* RPC from project */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Responsible Partner Co.</Label>
              {companyOptions.length > 0 ? (
                <Select value={form.responsible_partner_company || '__none__'} onValueChange={handleRpcChange}>
                  <SelectTrigger data-testid="rpc-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {companyOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.responsible_partner_company} onChange={(e) => setForm(p => ({ ...p, responsible_partner_company: e.target.value }))} placeholder="Company" data-testid="rpc-input" />
              )}
            </div>
            {/* Originator from project */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Originator</Label>
              {companyOptions.length > 0 ? (
                <Select value={form.originator || '__none__'} onValueChange={handleOriginatorChange}>
                  <SelectTrigger data-testid="originator-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {companyOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.originator} onChange={(e) => setForm(p => ({ ...p, originator: e.target.value }))} placeholder="Originator" data-testid="originator-input" />
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="save-module-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-brex-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              {generating ? 'Generating...' : 'Generate BREX Module'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing Modules */}
      {modules.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>Generated BREX Modules ({modules.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="brex-modules-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">DMC</th>
                    <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Security</th>
                    <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Issue</th>
                    <th className="text-left p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                    <th className="text-right p-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(m => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="p-2.5 text-sm font-mono text-teal-700">{m.dmc}</td>
                      <td className="p-2.5 text-sm text-slate-600">{m.security_classification}</td>
                      <td className="p-2.5 text-sm text-slate-600">{m.issue_number}-{m.in_work}</td>
                      <td className="p-2.5 text-sm text-slate-500">{new Date(m.created_at).toLocaleDateString()}</td>
                      <td className="p-2.5 text-right">
                        <Button variant="outline" size="sm" onClick={() => handleDelete(m.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-module-${m.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ═════ GENERATED DOCUMENTS TAB ═════ */
const GeneratedDocumentsTab = ({ projectId, token, refreshTrigger }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/brex-builder/generated-docs/${projectId}`, { headers });
      setDocuments(res.data || []);
    } catch {
      setDocuments([]);
    }
    setLoading(false);
  }, [projectId, headers]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments, refreshTrigger]);

  const handleRedownload = async (doc) => {
    try {
      let res;
      if (doc.doc_type === 'brdp_summary_pdf') {
        res = await axios.post(`${API}/api/brex-builder/generate-brdp-pdf`, {
          project_id: projectId, issue: doc.issue,
        }, { headers, responseType: 'blob' });
      } else if (doc.doc_type === 'brex_module_xml') {
        // Re-generate would need full settings; for now show a message
        toast.info('Use the BREX Module tab to re-generate this file');
        return;
      } else if (doc.doc_type === 'business_rules_pdf' || doc.doc_type === 'business_rules_xml') {
        toast.info('Use the BREX Module tab to re-generate this file');
        return;
      } else {
        toast.error('Unknown document type');
        return;
      }
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `${doc.name}`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (err) {
      const errorMsg = err.response?.data instanceof Blob
        ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
        : err.response?.data?.detail || 'Failed to download document';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/api/brex-builder/generated-docs/${docId}`, { headers });
      toast.success('Document record deleted');
      fetchDocuments();
    } catch { toast.error('Failed to delete document'); }
  };

  if (loading) {
    return <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="generated-documents-tab">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Download className="w-5 h-5 text-teal-600" /> Generated Documents
          </CardTitle>
          <CardDescription>Documents generated from your BREX Builder configuration</CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No Documents Generated</p>
              <p className="text-sm mt-1">Use the &ldquo;Generate PDF&rdquo; button on the BRDPs tab to create documents.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid={`gen-doc-${doc.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-teal-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{doc.name}</p>
                      <p className="text-xs text-slate-500">{doc.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[10px]">
                          {doc.doc_type === 'brdp_summary_pdf' ? 'BRDP PDF' :
                           doc.doc_type === 'brex_module_xml' ? 'BREX XML' :
                           doc.doc_type === 'business_rules_pdf' ? 'Rules PDF' :
                           doc.doc_type === 'business_rules_xml' ? 'Rules XML' :
                           doc.doc_type}
                        </Badge>
                        <span className="text-[10px] text-slate-400">{new Date(doc.created_at).toLocaleString()}</span>
                        <span className="text-[10px] text-slate-400">by {doc.created_by}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleRedownload(doc)} className="gap-1.5" data-testid={`download-doc-${doc.id}`}>
                      <Download className="w-3.5 h-3.5" /> Download
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(doc.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


/* ═════ MAIN PAGE ═════ */
const BREXBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const projects = useProjects();
  const isAdmin = user?.role === 'admin';

  const initialProject = location.state?.project;
  const initialProjectId = location.state?.projectId || initialProject?.id;

  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'none');
  const [currentProject, setCurrentProject] = useState(initialProject || null);
  const [activeTab, setActiveTab] = useState('brdps');
  const [selectedIssue, setSelectedIssue] = useState('');
  const [docsRefreshTrigger, setDocsRefreshTrigger] = useState(0);
  const triggerDocsRefresh = useCallback(() => setDocsRefreshTrigger(c => c + 1), []);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (selectedProject && selectedProject !== 'none' && !currentProject) {
      const p = projects.find(pr => pr.id === selectedProject);
      if (p) setCurrentProject(p);
    }
  }, [selectedProject, projects, currentProject]);

  const handleProjectChange = async (val) => {
    setSelectedProject(val);
    if (val !== 'none') {
      try {
        const res = await axios.get(`${API}/api/projects/${val}`, { headers });
        setCurrentProject(res.data);
      } catch {
        const p = projects.find(pr => pr.id === val);
        if (p) setCurrentProject(p);
      }
    } else {
      setCurrentProject(null);
    }
  };

  const effectiveProjectId = selectedProject !== 'none' ? selectedProject : (isAdmin ? 'admin-testing' : null);
  const effectiveProject = selectedProject !== 'none' ? currentProject : null;

  return (
    <div className="space-y-6" data-testid="brex-builder-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <button onClick={() => navigate(selectedProject !== 'none' ? `/dashboard/projects/${selectedProject}` : '/dashboard/projects')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 transition-colors mb-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />{selectedProject !== 'none' ? 'Back to Project' : 'Back to Projects'}
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>BREX Builder</h1>
          <p className="text-slate-500">Build and manage Business Rules Exchange (BREX) data modules</p>
        </div>
      </div>

      {/* Project Selection */}
      {!isAdmin && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <FolderOpen className="w-5 h-5 text-teal-600 shrink-0" />
              <div className="flex-1">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</Label>
                <Select value={selectedProject} onValueChange={handleProjectChange}>
                  <SelectTrigger data-testid="project-select">
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a project...</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      {(selectedProject !== 'none' || isAdmin) && (
        <div className="flex gap-1 border-b border-slate-200">
          <button onClick={() => setActiveTab('brdps')} className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${activeTab === 'brdps' ? 'text-teal-700 border-teal-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-testid="tab-brdps">
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />BRDPs
          </button>
          <button onClick={() => setActiveTab('module')} className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${activeTab === 'module' ? 'text-teal-700 border-teal-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-testid="tab-module">
            <Wrench className="w-4 h-4 inline mr-1.5 -mt-0.5" />BREX Module
          </button>
          <button onClick={() => setActiveTab('generated')} className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${activeTab === 'generated' ? 'text-teal-700 border-teal-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-testid="tab-generated">
            <Download className="w-4 h-4 inline mr-1.5 -mt-0.5" />Generated Documents
          </button>
        </div>
      )}

      {/* Tab Content - tabs stay mounted to preserve state across tab switches */}
      {effectiveProjectId && (
        <div key={effectiveProjectId}>
          <div style={{ display: activeTab === 'brdps' ? 'block' : 'none' }}>
            <BrdpTab projectId={effectiveProjectId} token={token} onIssueChange={setSelectedIssue} onDocGenerated={triggerDocsRefresh} />
          </div>
          <div style={{ display: activeTab === 'module' ? 'block' : 'none' }}>
            <BrexModuleTab projectId={effectiveProjectId} project={effectiveProject} token={token} selectedIssue={selectedIssue} onDocGenerated={triggerDocsRefresh} />
          </div>
          <div style={{ display: activeTab === 'generated' ? 'block' : 'none' }}>
            <GeneratedDocumentsTab projectId={effectiveProjectId} token={token} refreshTrigger={docsRefreshTrigger} />
          </div>
        </div>
      )}

      {!isAdmin && selectedProject === 'none' && (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-16 text-center text-slate-500">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-lg">Select a Project</p>
            <p className="text-sm mt-1">Choose a project to start building BREX modules</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BREXBuilderPage;
